import { ActionIcon, Avatar, Box, Button, Group, Menu, Popover, Text, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCoins,
  IconDots,
  IconHelpCircle,
  IconLogout,
  IconPencil,
  IconRobotFace,
  IconSettings,
  IconUser,
} from "@tabler/icons-react";
import { forwardRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import NotificationBell from "@/components/notifications/NotificationBell";
import AppUsageGuideBot from "@/components/profile/AppUsageGuideBot";
import { appUsageGuideStepIndexForPath } from "@/shared/constants/appUsageGuide.const";
import { COLLABORATION_WALLET_PATH } from "@/shared/constants/collaborationWallet.const";
import { isExpoSignupUser } from "@/shared/constants/clientOnboarding.const";
import { FLEX_DEMO_MIN_VIEWPORT_MQ } from "@/flex-demo/flexDemo.const";
import { useFlexDemoSessionOptional } from "@/flex-demo/FlexDemoSessionContext";

export type AppHeaderUserActionsProps = {
  /**
   * `inline` — robot, bell, profile in a row.
   * `collapsed` — leftmost ⋯ opens a popover with the same three actions.
   */
  mode?: "inline" | "collapsed";
};

const AppHeaderUserActions = forwardRef<HTMLDivElement, AppHeaderUserActionsProps>(
  function AppHeaderUserActions({ mode = "inline" }, ref) {
    const navigate = useNavigate();
    const location = useLocation();
    const { userDetails, logout } = useUserContext();
    const flexDemo = useFlexDemoSessionOptional();
    const isTabletUp = useMediaQuery(FLEX_DEMO_MIN_VIEWPORT_MQ, true, {
      getInitialValueInEffect: true,
    });
    const userName = userDetails?.name?.trim() || "User";
    const expoSignupLocked = isExpoSignupUser(userDetails);
    const companyName = userDetails?.company?.legalName || userDetails?.name || "Guest";
    const gtid = userDetails?.company?.gtid || userDetails?.email || "User";
    const [botOpened, setBotOpened] = useState(false);
    const [botStepIndex, setBotStepIndex] = useState(0);
    const [overflowOpened, setOverflowOpened] = useState(false);

    const openBot = () => {
      setBotStepIndex(appUsageGuideStepIndexForPath(location.pathname));
      setBotOpened(true);
      setOverflowOpened(false);
    };

    const profileMenu = (
      <Menu shadow="md" width={230} position="bottom-end">
        <Menu.Target>
          <Avatar radius="xl" size="md" color="blue" style={{ cursor: "pointer" }}>
            {userName.charAt(0).toUpperCase()}
          </Avatar>
        </Menu.Target>
        <Menu.Dropdown>
          <Box px="sm" py={6}>
            <Group gap={8} wrap="nowrap">
              <Avatar radius="xl" size="sm" style={{ backgroundColor: "#eef2ff", color: "#1d4ed8" }}>
                <IconSettings size={14} />
              </Avatar>
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>
                  {companyName}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {gtid}
                </Text>
              </Box>
            </Group>
          </Box>
          <Menu.Divider />
          {expoSignupLocked ? null : (
            <>
          <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => navigate("/profile/edit")}>
            Edit Profile
          </Menu.Item>
          <Menu.Item leftSection={<IconCoins size={16} />} onClick={() => navigate(COLLABORATION_WALLET_PATH)}>
            Collaboration wallet
          </Menu.Item>
            </>
          )}
          <Menu.Item leftSection={<IconHelpCircle size={16} />} onClick={() => navigate("/settings")}>
            Settings
          </Menu.Item>
          {expoSignupLocked ? (
            <Menu.Item leftSection={<IconUser size={16} />} onClick={() => navigate("/account")}>
              My Profile
            </Menu.Item>
          ) : null}
          <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={logout}>
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );

    const robotButton = (
      <Tooltip label="How the platform works" withArrow position="bottom">
        <ActionIcon
          variant="filled"
          size="lg"
          radius="xl"
          aria-label="How the platform works"
          onClick={openBot}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconRobotFace size={22} />
        </ActionIcon>
      </Tooltip>
    );

    const demoActiveAccepted = Boolean(flexDemo?.demoActive && flexDemo.session.accepted);
    const demoButton =
      flexDemo && isTabletUp && !expoSignupLocked && !demoActiveAccepted ? (
      <Tooltip label="Start demo" withArrow position="bottom">
        <Button
          size="sm"
          radius="xl"
          h={42}
          px="md"
          onClick={() => {
            setOverflowOpened(false);
            flexDemo.startDemo();
          }}
          styles={{
            root: {
              backgroundColor: "#ffc300",
              color: "#04305c",
              fontWeight: 700,
              border: "none",
              "&:hover": { backgroundColor: "#e6b000" },
            },
            label: { color: "#04305c" },
          }}
        >
          Demo
        </Button>
      </Tooltip>
    ) : null;

    const iconRow = (
      <Group gap="sm" wrap="nowrap" align="center">
        {robotButton}
        {demoButton}
        <NotificationBell />
        {profileMenu}
      </Group>
    );

    return (
      <Box ref={ref} style={{ flexShrink: 0 }}>
        {mode === "collapsed" ? (
          <Popover
            opened={overflowOpened}
            onChange={setOverflowOpened}
            position="bottom-end"
            shadow="md"
            radius="md"
            width="auto"
          >
            <Popover.Target>
              <ActionIcon
                variant="light"
                size="lg"
                radius="xl"
                aria-label="Open header actions"
                aria-expanded={overflowOpened}
                onClick={() => setOverflowOpened((o) => !o)}
                color="primary"
              >
                <IconDots size={22} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown p="sm">{iconRow}</Popover.Dropdown>
          </Popover>
        ) : (
          iconRow
        )}
        <AppUsageGuideBot
          opened={botOpened}
          onClose={() => setBotOpened(false)}
          stepIndex={botStepIndex}
          onStepChange={setBotStepIndex}
        />
      </Box>
    );
  },
);

export default AppHeaderUserActions;

/** Use in page TitleComponent. Hidden on sm+ where AppShell already shows these actions. */
export function PageTitleHeaderUserActions() {
  return (
    <Box hiddenFrom="sm">
      <AppHeaderUserActions />
    </Box>
  );
}
